import { FilterOutlined } from '@ant-design/icons';
import { ColumnsType } from 'antd/es/table';
import { FilterDropdownProps } from 'antd/es/table/interface';
import dayjs, { Dayjs } from 'dayjs';
import * as React from 'react';

import { CellControllerKind } from 'entities/TableControllers';
import { useWindowDimensions } from 'hooks/';
import { TableFactoryRowModel, ITableColumnModel } from 'stores/common';
import { TABLE_FACTORY_DEFAULT_PARAMS } from 'stores/common/table/TableFactoryStore/config';
import { TableFactoryStoreParams } from 'stores/common/table/TableFactoryStore/types';
import { TableRowDataStore } from 'stores/common/table/TableRowDataStore';
import { getRandomDomId } from 'utils/getRandomDomId';

import { ControllersCellFactory } from './ControllersCellFactory';
import { DateFilter, DateFilterValue } from './DateFilter';
import HeadingCell from './HeadingCell';
import { NumberFilter, NumberFilterValue } from './NumberFilter';
import { SelectFilter, SelectFilterValue, SelectOption } from './SelectFilter';
import { SwitchFilter, SwitchFilterValue } from './SwitchFilter';
import { TextFilter, TextFilterValue } from './TextFilter';

export type ColumnInitConfig = {
  /** Сущности колонок */
  columns: ITableColumnModel[];
  tableParams: TableFactoryStoreParams;
  /** Строки таблицы для определения типа колонок (legacy режим) */
  rows?: TableFactoryRowModel[];
  /** Хранилище данных строк для виртуализированного режима */
  rowDataStore?: TableRowDataStore;
};

const CONFIG_DEFAULT_PARAMS: ColumnInitConfig = {
  columns: [],
  tableParams: TABLE_FACTORY_DEFAULT_PARAMS,
  rows: [],
};

const COLUMN_DEFAULT_WIDTH = 200;
const FILTER_ICON_WIDTH = 24;

/** Типы контроллеров, для которых показывается числовой фильтр */
const NUMERIC_CONTROLLER_KINDS = new Set([
  CellControllerKind.INPUT_NUMBER,
  CellControllerKind.NUMBER,
]);

/** Типы контроллеров, для которых показывается текстовый фильтр */
const TEXT_CONTROLLER_KINDS = new Set([
  CellControllerKind.TEXT,
  CellControllerKind.INPUT_STRING,
]);

/**
 * Получает тип контроллера колонки на основе первой строки
 */
const getColumnControllerKind = (
  columnId: string,
  rows: TableFactoryRowModel[],
  rowDataStore?: TableRowDataStore
): CellControllerKind | null => {
  // Сначала пробуем из rows (legacy режим)
  if (rows.length > 0) {
    const cell = rows[0].cells[columnId];
    return cell?.params.controllerKind ?? null;
  }
  
  // Fallback на rowDataStore (виртуализированный режим)
  if (rowDataStore && rowDataStore.rowCount > 0) {
    const firstRowId = rowDataStore.rowIds[0];
    const entity = rowDataStore.getRowEntity(firstRowId);
    return entity?.columns[columnId]?.controllerKind ?? null;
  }
  
  return null;
};

/**
 * Проверяет, является ли колонка числовой на основе типа контроллера первой строки
 */
const isNumericColumn = (
  columnId: string,
  rows: TableFactoryRowModel[],
  rowDataStore?: TableRowDataStore
): boolean => {
  const kind = getColumnControllerKind(columnId, rows, rowDataStore);
  return kind !== null && NUMERIC_CONTROLLER_KINDS.has(kind);
};

/**
 * Проверяет, является ли числовая колонка процентной
 */
const isPercentColumn = (
  columnId: string,
  rows: TableFactoryRowModel[],
  rowDataStore?: TableRowDataStore
): boolean => {
  // Пробуем из rows (legacy режим)
  if (rows.length > 0) {
    const cell = rows[0].cells[columnId];
    if (!cell) return false;
    
    const controllerValue = cell.controller.controllerValue;
    
    // Для NUMBER значение хранится как объект { value, format }
    if (typeof controllerValue === 'object' && controllerValue !== null && 'format' in controllerValue) {
      return (controllerValue as { format: string }).format === 'percent';
    }
    return false;
  }
  
  // Fallback на rowDataStore (виртуализированный режим)
  if (rowDataStore && rowDataStore.rowCount > 0) {
    const firstRowId = rowDataStore.rowIds[0];
    const entity = rowDataStore.getRowEntity(firstRowId);
    const cellEntity = entity?.columns[columnId];
    
    if (cellEntity && 'controller' in cellEntity) {
      // Для input-number format хранится на уровне controller.format
      const controller = cellEntity.controller as { format?: string };
      return controller?.format === 'percent';
    }
  }
  
  return false;
};

/**
 * Проверяет, является ли колонка текстовой на основе типа контроллера первой строки
 */
const isTextColumn = (
  columnId: string,
  rows: TableFactoryRowModel[],
  rowDataStore?: TableRowDataStore
): boolean => {
  const kind = getColumnControllerKind(columnId, rows, rowDataStore);
  return kind !== null && TEXT_CONTROLLER_KINDS.has(kind);
};

/**
 * Проверяет, является ли колонка типа SELECT
 */
const isSelectColumn = (
  columnId: string,
  rows: TableFactoryRowModel[],
  rowDataStore?: TableRowDataStore
): boolean => {
  const kind = getColumnControllerKind(columnId, rows, rowDataStore);
  return kind === CellControllerKind.SELECT;
};

/**
 * Проверяет, является ли колонка типа SWITCH
 */
const isSwitchColumn = (
  columnId: string,
  rows: TableFactoryRowModel[],
  rowDataStore?: TableRowDataStore
): boolean => {
  const kind = getColumnControllerKind(columnId, rows, rowDataStore);
  return kind === CellControllerKind.SWITCH;
};

/**
 * Проверяет, является ли колонка типа INPUT_DATE
 */
const isDateColumn = (
  columnId: string,
  rows: TableFactoryRowModel[],
  rowDataStore?: TableRowDataStore
): boolean => {
  const kind = getColumnControllerKind(columnId, rows, rowDataStore);
  return kind === CellControllerKind.INPUT_DATE;
};

/**
 * Фильтрует числовое значение по заданным критериям
 */
const filterNumberValue = (
  filterValue: NumberFilterValue,
  cellValue: string | number | undefined
): boolean => {
  if (cellValue === undefined || cellValue === null) return false;
  
  const num = typeof cellValue === 'string' ? parseFloat(cellValue) : cellValue;
  
  if (isNaN(num)) return false;
  
  const { from, to, strict } = filterValue;
  
  // Проверка нижней границы
  if (from !== undefined) {
    if (strict ? num <= from : num < from) return false;
  }
  
  // Проверка верхней границы
  if (to !== undefined) {
    if (strict ? num >= to : num > to) return false;
  }
  
  return true;
};

/**
 * Получает числовое значение из ячейки
 */
const getCellNumericValue = (
  record: TableFactoryRowModel,
  columnId: string
): string | number | undefined => {
  const cell = record.cells[columnId];
  if (!cell) return undefined;
  
  const controllerValue = cell.controller.controllerValue;
  
  // Для INPUT_NUMBER значение хранится как строка
  if (typeof controllerValue === 'string') {
    return controllerValue;
  }
  
  // Для NUMBER значение хранится как объект { value, format }
  if (typeof controllerValue === 'object' && controllerValue !== null && 'value' in controllerValue) {
    return (controllerValue as { value: number }).value;
  }
  
  return undefined;
};

/**
 * Получает текстовое значение из ячейки
 */
const getCellTextValue = (
  record: TableFactoryRowModel,
  columnId: string
): string => {
  const cell = record.cells[columnId];
  if (!cell) return '';
  
  const controllerValue = cell.controller.controllerValue;
  
  if (typeof controllerValue === 'string') {
    return controllerValue;
  }
  
  return '';
};

/**
 * Фильтрует текстовое значение по подстроке (регистронезависимо)
 */
const filterTextValue = (search: string, cellValue: string): boolean => {
  if (!search.trim()) return true;
  return cellValue.toLowerCase().includes(search.toLowerCase());
};

/**
 * Получает опции SELECT из первой строки
 */
const getSelectOptions = (
  columnId: string,
  rows: TableFactoryRowModel[],
  rowDataStore?: TableRowDataStore
): SelectOption[] => {
  // Пробуем из rows (legacy режим)
  if (rows.length > 0) {
    const cell = rows[0].cells[columnId];
    if (!cell) return [];
    
    const controllerValue = cell.controller.controllerValue;
    
    // Для SELECT controllerValue имеет структуру { options: [...], value: {...} }
    if (
      typeof controllerValue === 'object' &&
      controllerValue !== null &&
      'options' in controllerValue
    ) {
      const options = (controllerValue as { options: Array<{ id: string; value: string }> }).options;
      return options.map((opt) => ({ id: opt.id, value: opt.value }));
    }
    return [];
  }
  
  // Fallback на rowDataStore (виртуализированный режим)
  if (rowDataStore && rowDataStore.rowCount > 0) {
    const firstRowId = rowDataStore.rowIds[0];
    const entity = rowDataStore.getRowEntity(firstRowId);
    const cellEntity = entity?.columns[columnId];
    
    if (cellEntity && 'controller' in cellEntity) {
      const controller = cellEntity.controller as { options?: Array<{ id: string; value: string }> };
      if (controller?.options) {
        return controller.options.map((opt) => ({ id: opt.id, value: opt.value }));
      }
    }
  }
  
  return [];
};

/**
 * Получает выбранное значение SELECT из ячейки
 */
const getCellSelectValue = (
  record: TableFactoryRowModel,
  columnId: string
): string | undefined => {
  const cell = record.cells[columnId];
  if (!cell) return undefined;
  
  const controllerValue = cell.controller.controllerValue;
  
  // Для SELECT controllerValue имеет структуру { options: [...], value: { id, value } }
  if (
    typeof controllerValue === 'object' &&
    controllerValue !== null &&
    'value' in controllerValue
  ) {
    const value = (controllerValue as { value: { value: string } | null }).value;
    return value?.value;
  }
  
  return undefined;
};

/**
 * Фильтрует значение SELECT по выбранным опциям
 */
const filterSelectValue = (
  selected: string[],
  cellValue: string | undefined
): boolean => {
  if (selected.length === 0) return true;
  if (!cellValue) return false;
  return selected.includes(cellValue);
};

/**
 * Получает boolean значение SWITCH из ячейки
 */
const getCellSwitchValue = (
  record: TableFactoryRowModel,
  columnId: string
): boolean | undefined => {
  const cell = record.cells[columnId];
  if (!cell) return undefined;
  
  const controllerValue = cell.controller.controllerValue;
  
  if (typeof controllerValue === 'boolean') {
    return controllerValue;
  }
  
  return undefined;
};

/**
 * Фильтрует значение SWITCH по выбранным опциям
 */
const filterSwitchValue = (
  selected: boolean[],
  cellValue: boolean | undefined
): boolean => {
  if (selected.length === 0) return true;
  if (cellValue === undefined) return false;
  return selected.includes(cellValue);
};

/**
 * Получает Dayjs значение из ячейки INPUT_DATE
 */
const getCellDateValue = (
  record: TableFactoryRowModel,
  columnId: string
): Dayjs | null | undefined => {
  const cell = record.cells[columnId];
  if (!cell) return undefined;
  
  const controllerValue = cell.controller.controllerValue;
  
  // Для INPUT_DATE controllerValue — это Dayjs | null
  if (controllerValue === null) {
    return null;
  }
  
  if (typeof controllerValue === 'object' && 'isValid' in controllerValue) {
    return controllerValue as Dayjs;
  }
  
  return undefined;
};

/**
 * Фильтрует дату по заданному диапазону
 */
const filterDateValue = (
  filterValue: DateFilterValue,
  cellValue: Dayjs | null | undefined
): boolean => {
  if (!cellValue) return false;
  
  const { from, to } = filterValue;
  
  // Проверка нижней границы
  if (from) {
    const fromDate = dayjs(from);
    if (cellValue.isBefore(fromDate)) {
      return false;
    }
  }
  
  // Проверка верхней границы
  if (to) {
    const toDate = dayjs(to);
    if (cellValue.isAfter(toDate)) {
      return false;
    }
  }
  
  return true;
};

export const useColumns = ({
  columns,
  tableParams,
  rows = [],
  rowDataStore,
}: ColumnInitConfig = CONFIG_DEFAULT_PARAMS): ColumnsType<TableFactoryRowModel> => {
  return React.useMemo(() => {
    return columns.map((item, index) => {
      const columnId = item.data.id;
      const isNumeric = isNumericColumn(columnId, rows, rowDataStore);
      const isText = isTextColumn(columnId, rows, rowDataStore);
      
      const baseColumn = {
        title: (
          <HeadingCell
            tableParams={tableParams}
            title={item.data.name}
            icons={item.data.icons}
          />
        ),
        sorter: item.data.sortable,
        dataIndex: columnId,
        width: COLUMN_DEFAULT_WIDTH,
        /** Важно!!! Считаем, что первая приходящая колонка у нас всегда зафиксирована */
        fixed: index === 0,
        key: item.data.name,
        render: (_: unknown, record: TableFactoryRowModel) => {
          return (
            <ControllersCellFactory
              record={record.cells[columnId]}
              row={record}
              tableParams={tableParams}
            />
          );
        },
      };
      
      // Добавляем фильтр для числовых колонок
      if (isNumeric) {
        const isPercent = isPercentColumn(columnId, rows, rowDataStore);
        return {
          ...baseColumn,
          width: COLUMN_DEFAULT_WIDTH + FILTER_ICON_WIDTH,
          filterDropdown: (props: FilterDropdownProps) => (
            <NumberFilter
              selectedKeys={props.selectedKeys}
              setSelectedKeys={props.setSelectedKeys}
              confirm={props.confirm}
              clearFilters={props.clearFilters}
              close={props.close}
              isPercent={isPercent}
            />
          ),
          filterIcon: (filtered: boolean) => (
            <FilterOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
          ),
          onFilter: (value: React.Key | boolean, record: TableFactoryRowModel) => {
            if (typeof value !== 'string') return true;
            
            try {
              const filterValue = JSON.parse(value) as NumberFilterValue;
              const cellValue = getCellNumericValue(record, columnId);
              return filterNumberValue(filterValue, cellValue);
            } catch {
              return true;
            }
          },
        };
      }
      
      // Добавляем фильтр для текстовых колонок
      if (isText) {
        return {
          ...baseColumn,
          width: COLUMN_DEFAULT_WIDTH + FILTER_ICON_WIDTH,
          filterDropdown: (props: FilterDropdownProps) => (
            <TextFilter
              selectedKeys={props.selectedKeys}
              setSelectedKeys={props.setSelectedKeys}
              confirm={props.confirm}
              clearFilters={props.clearFilters}
              close={props.close}
            />
          ),
          filterIcon: (filtered: boolean) => (
            <FilterOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
          ),
          onFilter: (value: React.Key | boolean, record: TableFactoryRowModel) => {
            if (typeof value !== 'string') return true;
            
            try {
              const filterValue = JSON.parse(value) as TextFilterValue;
              const cellValue = getCellTextValue(record, columnId);
              
              if (filterValue.mode === 'number') {
                // Режим числового фильтра
                const numValue = parseFloat(cellValue);
                if (isNaN(numValue)) return false;
                
                const { from, to, strict } = filterValue;
                
                if (from !== undefined) {
                  if (strict ? numValue <= from : numValue < from) return false;
                }
                if (to !== undefined) {
                  if (strict ? numValue >= to : numValue > to) return false;
                }
                
                return true;
              } else {
                // Режим текстового фильтра (по умолчанию)
                return filterTextValue(filterValue.search || '', cellValue);
              }
            } catch {
              return true;
            }
          },
        };
      }
      
      // Добавляем фильтр для SELECT колонок
      const isSelect = isSelectColumn(columnId, rows, rowDataStore);
      if (isSelect) {
        const options = getSelectOptions(columnId, rows, rowDataStore);
        return {
          ...baseColumn,
          width: COLUMN_DEFAULT_WIDTH + FILTER_ICON_WIDTH,
          filterDropdown: (props: FilterDropdownProps) => (
            <SelectFilter
              selectedKeys={props.selectedKeys}
              setSelectedKeys={props.setSelectedKeys}
              confirm={props.confirm}
              clearFilters={props.clearFilters}
              close={props.close}
              options={options}
            />
          ),
          filterIcon: (filtered: boolean) => (
            <FilterOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
          ),
          onFilter: (value: React.Key | boolean, record: TableFactoryRowModel) => {
            if (typeof value !== 'string') return true;
            
            try {
              const filterValue = JSON.parse(value) as SelectFilterValue;
              const cellValue = getCellSelectValue(record, columnId);
              return filterSelectValue(filterValue.selected, cellValue);
            } catch {
              return true;
            }
          },
        };
      }
      
      // Добавляем фильтр для SWITCH колонок
      const isSwitch = isSwitchColumn(columnId, rows, rowDataStore);
      if (isSwitch) {
        return {
          ...baseColumn,
          width: COLUMN_DEFAULT_WIDTH + FILTER_ICON_WIDTH,
          filterDropdown: (props: FilterDropdownProps) => (
            <SwitchFilter
              selectedKeys={props.selectedKeys}
              setSelectedKeys={props.setSelectedKeys}
              confirm={props.confirm}
              clearFilters={props.clearFilters}
              close={props.close}
            />
          ),
          filterIcon: (filtered: boolean) => (
            <FilterOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
          ),
          onFilter: (value: React.Key | boolean, record: TableFactoryRowModel) => {
            if (typeof value !== 'string') return true;
            
            try {
              const filterValue = JSON.parse(value) as SwitchFilterValue;
              const cellValue = getCellSwitchValue(record, columnId);
              return filterSwitchValue(filterValue.selected, cellValue);
            } catch {
              return true;
            }
          },
        };
      }
      
      // Добавляем фильтр для INPUT_DATE колонок
      const isDate = isDateColumn(columnId, rows, rowDataStore);
      if (isDate) {
        return {
          ...baseColumn,
          width: COLUMN_DEFAULT_WIDTH + FILTER_ICON_WIDTH,
          filterDropdown: (props: FilterDropdownProps) => (
            <DateFilter
              selectedKeys={props.selectedKeys}
              setSelectedKeys={props.setSelectedKeys}
              confirm={props.confirm}
              clearFilters={props.clearFilters}
              close={props.close}
            />
          ),
          filterIcon: (filtered: boolean) => (
            <FilterOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
          ),
          onFilter: (value: React.Key | boolean, record: TableFactoryRowModel) => {
            if (typeof value !== 'string') return true;
            
            try {
              const filterValue = JSON.parse(value) as DateFilterValue;
              const cellValue = getCellDateValue(record, columnId);
              return filterDateValue(filterValue, cellValue);
            } catch {
              return true;
            }
          },
        };
      }
      
      return baseColumn;
    });
  }, [columns, tableParams, rows, rowDataStore]);
};

// 32px (layout padding) + 54px (VirtualSummaryRow footer) + 10px (buffer)
const PADDING_BOTTOM = 96;

export const useFactoryTableHeight = () => {
  const [y, setY] = React.useState<number>(0);

  const ref = React.useRef<HTMLDivElement | null>(null);

  const { height } = useWindowDimensions();

  const id = getRandomDomId();

  React.useEffect(() => {
    const element = document.querySelector(`#${id} thead`);

    if (ref.current && element) {
      setY(
        height -
          ref.current?.getBoundingClientRect().top -
          PADDING_BOTTOM -
          element.clientHeight
      );
    }
  }, [height, id]);

  return {
    ref,
    y,
    id,
  };
};

/**
 * Применяет фильтр к одной строке для виртуального режима
 */
const applyRowFilter = (
  record: TableFactoryRowModel,
  columnId: string,
  filterValue: string
): boolean => {
  try {
    const parsed = JSON.parse(filterValue);
    
    // Определяем тип фильтра по структуре значения
    if ('mode' in parsed) {
      // TextFilter
      const textFilter = parsed as TextFilterValue;
      const cellValue = getCellTextValue(record, columnId);
      
      if (textFilter.mode === 'number') {
        const numValue = parseFloat(cellValue);
        if (isNaN(numValue)) return false;
        
        const { from, to, strict } = textFilter;
        
        if (from !== undefined) {
          if (strict ? numValue <= from : numValue < from) return false;
        }
        if (to !== undefined) {
          if (strict ? numValue >= to : numValue > to) return false;
        }
        return true;
      } else {
        return filterTextValue(textFilter.search || '', cellValue);
      }
    } else if ('selected' in parsed && Array.isArray(parsed.selected)) {
      // SelectFilter или SwitchFilter — оба используют { selected: [...] }
      if (parsed.selected.length === 0) return true;
      
      if (typeof parsed.selected[0] === 'boolean') {
        // SwitchFilter: { selected: boolean[] }
        const cellValue = getCellSwitchValue(record, columnId);
        return parsed.selected.includes(cellValue);
      } else {
        // SelectFilter: { selected: string[] }
        const cellValue = getCellSelectValue(record, columnId);
        return cellValue !== undefined && parsed.selected.includes(cellValue);
      }
    } else if ('from' in parsed || 'to' in parsed) {
      // NumberFilter or DateFilter
      if (typeof parsed.from === 'string' || typeof parsed.to === 'string') {
        // DateFilter
        const dateFilter = parsed as DateFilterValue;
        const cellValue = getCellDateValue(record, columnId);
        return filterDateValue(dateFilter, cellValue);
      } else {
        // NumberFilter
        const numberFilter = parsed as NumberFilterValue;
        const cellValue = getCellNumericValue(record, columnId);
        return filterNumberValue(numberFilter, cellValue);
      }
    }
    
    return true;
  } catch {
    return true;
  }
};

/**
 * Хук для фильтрации строк в виртуальном режиме таблицы
 */
export const useVirtualFiltering = (
  rows: TableFactoryRowModel[],
  filters: Record<string, (string | number | boolean)[] | null> | null,
  columnNameToIdMap: Record<string, string> = {}
): TableFactoryRowModel[] => {
  return React.useMemo(() => {
    if (!filters) return rows;
    
    const activeFilters = Object.entries(filters).filter(
      ([, values]) => values !== null && values.length > 0
    );
    
    if (activeFilters.length === 0) return rows;
    
    return rows.filter((record) => {
      return activeFilters.every(([filterKey, values]) => {
        if (!values || values.length === 0) return true;
        
        // Преобразуем ключ фильтра (название колонки) в ID колонки
        const columnId = columnNameToIdMap[filterKey] || filterKey;
        
        // Берём первое значение фильтра (для текстовых/числовых фильтров это JSON строка)
        const filterValue = values[0];
        if (typeof filterValue !== 'string') return true;
        
        return applyRowFilter(record, columnId, filterValue);
      });
    });
  }, [rows, filters, columnNameToIdMap]);
};
